import { JOB_DESCRIPTION_AUTHORITIES, JobAuthorify } from '@business/entities/library';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { Sortable, SortableList } from '@modules/common';
import React, { memo, useCallback, useEffect } from 'react';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { Col, Label, Row } from 'reactstrap';
import { FunctionFormType } from './FormViewModal';
import { FCFunctionFormData } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/FCFunction';
import { FormArray } from '@core/utils/form';
import { R } from '@core/utils/r';
import { FormInput, FormSelect } from '@core/components/form';
import { CommonService } from '@business/services';
import { SortHandle } from '@modules/common/SortHandle';

export interface FunctionItemProps {
  i: number;
  ctrl: FunctionFormType;
  form: FormArray<FunctionFormType>;
  canDelete: boolean;
  onEdit(): void;
  listeners?: any;
}

export const FunctionItem = memo((props: FunctionItemProps) => {
  const { ctrl, i, form, canDelete, listeners } = props;
  const formValue = useNonNilObservable(ctrl.value$);

  const DragSortable = SortableHandle(() => (
    <a className="btn btn-outline-primary" style={{ minWidth: '40px' }}>
      <i className="fas fa-sort" style={{ cursor: 'move' }} />
    </a>
  ));
  if (!formValue.isEdit) {
    return (
      <div
        key={`function-${i}`}
        className={`d-flex flex-fill p-2 w-100 mt-${i ? 4 : 0}`}
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0, // Prevents overflow issues
          boxSizing: 'border-box',
        }}
      >
        <Row className="w-100 ml-0">
          <Col md={10} style={{ paddingLeft: 0, paddingRight: '8px' }}>
            ({[JobAuthorify.getApproval, JobAuthorify.handle, JobAuthorify.act].indexOf(formValue.authorityLevel) + 1}) {formValue.function}
          </Col>
          <Col md={2} style={{ paddingLeft: '8px', paddingRight: 0 }}>
            <div className={`actions d-flex py-2`}>
              <a
                className="d-flex mr-2"
                onClick={() => {
                  props.onEdit();
                  ctrl.patchValue({ isEdit: true });
                }}
              >
                <i className=" bx bx-edit font-size-16" />
              </a>
              {canDelete && (
                <a className="d-flex mr-2" onClick={() => form.removeAt(i)}>
                  <i className="bx bx-trash font-size-16 text-danger"></i>
                </a>
              )}
              <SortHandle listeners={listeners} />
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <>
      <div
        key={`function-${i}`}
        className={`d-flex flex-column flex-fill p-2 ml-3 mt-${i ? 4 : 0}`}
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <Row className="w-100">
          <Col md={7} style={{ paddingLeft: 0, paddingRight: '8px' }}>
            <FormInput type="textarea" control={ctrl.controls.function} rows={4} />
          </Col>
          <Col md={1} style={{ paddingLeft: '8px', paddingRight: 0 }}>
            <DragSortable />
            <br />
            {canDelete && (
              <a className="btn btn-outline-danger mt-2" onClick={() => form.removeAt(i)} style={{ minWidth: '40px' }}>
                <i className="bx bx-x"></i>
              </a>
            )}
          </Col>
          <Col md={3} style={{ padding: 0, paddingTop: '8px' }}>
            <FormSelect
              control={ctrl.controls.authorityLevel}
              items={CommonService.selectItems(
                [JobAuthorify.getApproval, JobAuthorify.handle, JobAuthorify.act],
                (i, index) => `${index + 1}. ${JOB_DESCRIPTION_AUTHORITIES[i]}`,
                i => i,
              )}
            />
          </Col>
        </Row>
      </div>
    </>
  );
});

export interface FormGridViewProps {
  form: FormArray<FunctionFormType>;
  field: FCFunctionFormData;
}

export const FormFunctionView = memo((props: FormGridViewProps) => {
  const { form, field } = props;
  const functionValues = useObservable(form.value$, []);
  const functions = useObservable(form.items$, []);

  //   const onSortEndOld = useCallback(
  //     (sort: SortEnd) => {
  //       const oldIndex = sort.oldIndex;
  //       const newIndex = sort.newIndex;
  //       const newStep = R.move(functionValues, oldIndex, newIndex);
  //       form.setValue(newStep);
  //     },
  //     [form, functions],
  //   );

  const onSortEnd = useCallback(
    (newFunctions: any[]) => {
      form.setValue(newFunctions);
    },
    [form],
  );

  useEffect(() => {
    if (!functions.length) form.push();
  }, [functions, form]);

  const onEditItem = useCallback(() => {
    functions.forEach(ctr => ctr.patchValue({ isEdit: false }));
  }, [functions]);

  return (
    <div className="w-100">
      <Label className="mt-2">{field.title}</Label>
      <Sortable
        handle
        items={functionValues}
        getId={(f, i) => i.toString()}
        onSort={onSortEnd}
        lockAxis="y"
        direction="vertical"
        useDragOverlay={true}
        getItemStyles={({ isDragOverlay }) => ({
          width: '100%',
        })}
        wrapperStyle={({ isDragging }) => ({
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        })}
        renderItem={(value, index, listeners) => {
          const ctrl = R.get(functions, index);
          if (!ctrl) return <div />;
          return (
            <FunctionItem
              onEdit={onEditItem}
              key={ctrl.uid}
              i={index}
              form={form}
              ctrl={ctrl}
              canDelete={functions.length > 1}
              listeners={listeners}
            />
          );
        }}
      />
      <Row className="mb-3">
        <Col md={12}>
          <a
            className="btn btn-outline-primary"
            onClick={() => {
              functions.forEach(ctr => ctr.patchValue({ isEdit: false }));
              form.push();
            }}
          >
            Add Function
          </a>
        </Col>
      </Row>
    </div>
  );
});
